import type { ClientMsg, ClientTransport, ServerMsg } from "@rivalwatch/shared";

/** Browser WebRTC transport: server creates the offer (two data channels), we answer. */
export async function connectRtc(room: string, timeoutMs = 8000): Promise<ClientTransport> {
  const res = await fetch("/rtc/offer", { method: "POST", body: JSON.stringify({ room }) });
  if (!res.ok) throw new Error(`Signaling failed (${res.status})`);
  const offer = (await res.json()) as { id: string; sdp: RTCSessionDescriptionInit };
  const pc = new RTCPeerConnection();
  let rel: RTCDataChannel | undefined;
  let unrel: RTCDataChannel | undefined;
  const t: ClientTransport = {
    sendReliable: (m: ClientMsg) => {
      if (rel?.readyState === "open") rel.send(JSON.stringify(m));
    },
    sendUnreliable: (d: Uint8Array) => {
      if (unrel?.readyState === "open" && unrel.bufferedAmount < 64 * 1024) unrel.send(d as Uint8Array<ArrayBuffer>);
    },
    close: () => pc.close(),
  };
  const ready = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("WebRTC connection timed out")), timeoutMs);
    let open = 0;
    pc.ondatachannel = (e) => {
      const dc = e.channel;
      dc.binaryType = "arraybuffer";
      if (dc.label === "r") {
        rel = dc;
        dc.onmessage = (m) => typeof m.data === "string" && t.onReliable?.(JSON.parse(m.data) as ServerMsg);
      } else {
        unrel = dc;
        dc.onmessage = (m) => m.data instanceof ArrayBuffer && t.onUnreliable?.(new Uint8Array(m.data));
      }
      dc.onclose = () => t.onClose?.();
      const opened = () => {
        if (++open === 2) {
          clearTimeout(timer);
          resolve();
        }
      };
      if (dc.readyState === "open") opened();
      else dc.onopen = opened;
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") {
        clearTimeout(timer);
        reject(new Error("WebRTC connection failed"));
        t.onClose?.();
      }
      if (pc.connectionState === "disconnected" || pc.connectionState === "closed") t.onClose?.();
    };
  });
  await pc.setRemoteDescription(offer.sdp);
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  await fetch("/rtc/answer", { method: "POST", body: JSON.stringify({ id: offer.id, sdp: pc.localDescription }) });
  try {
    await ready;
  } catch (e) {
    pc.close();
    throw e;
  }
  return t;
}

/** WebSocket fallback (TCP) — used when UDP is blocked or with ?transport=ws. */
export async function connectWs(room: string): Promise<ClientTransport> {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/ws?room=${encodeURIComponent(room)}`);
  ws.binaryType = "arraybuffer";
  const t: ClientTransport = {
    sendReliable: (m) => ws.readyState === 1 && ws.send(JSON.stringify(m)),
    sendUnreliable: (d) => ws.readyState === 1 && ws.bufferedAmount < 64 * 1024 && ws.send(d as Uint8Array<ArrayBuffer>),
    close: () => ws.close(),
  };
  ws.onmessage = (e) => {
    if (typeof e.data === "string") t.onReliable?.(JSON.parse(e.data));
    else t.onUnreliable?.(new Uint8Array(e.data as ArrayBuffer));
  };
  ws.onclose = () => t.onClose?.();
  await new Promise<void>((resolve, reject) => {
    ws.onopen = () => resolve();
    ws.onerror = () => reject(new Error("WebSocket connection failed"));
  });
  return t;
}

export async function connect(room: string, prefer: string | null): Promise<{ transport: ClientTransport; kind: "webrtc" | "ws" }> {
  if (prefer === "ws") return { transport: await connectWs(room), kind: "ws" };
  try {
    return { transport: await connectRtc(room), kind: "webrtc" };
  } catch (e) {
    console.warn("WebRTC unavailable, falling back to WebSocket:", e);
    return { transport: await connectWs(room), kind: "ws" };
  }
}
